import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { CheckCircleOutlineRounded, ErrorOutlineRounded, WarningRounded } from '@material-ui/icons';
import InputText from '../../../commonModules/interface/form-input-inline';
import {
    getIntegration,
    saveIntegration,
    testIntegration,
    syncIntegration,
    fetchIntegrationBoxes,
} from '../../../../../../jsx/actions/settingsAction';
import ProspectBoxSelectionDialog from './ProspectBoxSelectionDialog';
import ProspectPushLogTable from './ProspectPushLogTable';

const INTEGRATION_TYPE = 'prospect';
const DEFAULT_BASE_URL = 'https://nefco.prospect.energy/api/v1';

const CATEGORY_LABELS = {
    customers: 'Clients',
    installations: 'Installations',
    payments_ts: 'Paiements',
};

// Ordre d'affichage stable des catégories, indépendant de l'ordre des clés renvoyées par le backend.
const CATEGORY_ORDER = ['customers', 'installations', 'payments_ts'];

// Fenêtre max de polling après un déclenchement de synchro (garde-fou si le backend
// ne repasse jamais sync.running à false, ex. crash worker) — 10 minutes.
const MAX_POLL_MS = 10 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;

class ProspectIntegrationForm extends Component {
    state = {
        enabled: false,
        baseUrl: DEFAULT_BASE_URL,
        paymentsSince: '',
        // Mode simulation de compte (settings.simulation) : indépendant de outboundEnabled
        // (état de l'environnement, non éditable) — les deux se combinent côté backend.
        simulation: false,
        secretCustomers: '',
        secretInstallations: '',
        secretPaymentsTs: '',
        // Un secret marqué "à effacer" est envoyé dans clearSecrets au PUT, jamais dans secrets
        // (les deux en même temps pour la même catégorie sont mutuellement exclusifs).
        clearCustomers: false,
        clearInstallations: false,
        clearPaymentsTs: false,
        loaded: false,
        boxDialogOpen: false,
        // Avertissement affiché après un enregistrement de la sélection qui a ajouté des box
        // (le backend a remis les curseurs à zéro : historique des paiements renvoyé).
        cursorsResetNotice: false,
        // Incrémenté pour recharger le journal des envois (fin de test, fin de synchro).
        logsReloadKey: 0,
    };

    // Timer de polling — instance field, pas de state (ne doit pas déclencher de re-render).
    pollTimer = null;
    pollStartedAt = null;

    componentDidMount() {
        this.props.getIntegration(INTEGRATION_TYPE);
        this.props.fetchIntegrationBoxes(INTEGRATION_TYPE);
    }

    componentDidUpdate(prevProps) {
        const { prospect } = this.props;
        // Fin de synchro (running true → false) : de nouvelles entrées sont au journal.
        const wasRunning = !!(prevProps.prospect && prevProps.prospect.sync && prevProps.prospect.sync.running);
        const running = !!(prospect && prospect.sync && prospect.sync.running);
        if (wasRunning && !running) {
            this.reloadLogs();
        }
        // Pré-remplissage une fois chargé (jamais les secrets — champs write-only).
        if (!this.state.loaded && prospect && !prospect.loading) {
            this.setState({
                loaded: true,
                enabled: !!prospect.enabled,
                baseUrl: (prospect.settings && prospect.settings.baseUrl) || DEFAULT_BASE_URL,
                paymentsSince: (prospect.settings && prospect.settings.paymentsSince) || '',
                simulation: !!(prospect.settings && prospect.settings.simulation === 'true'),
            });
        }
        this.maybeTogglePolling();
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    // Tant que sync.running est vrai (déclenché ici ou constaté au chargement), on rafraîchit
    // le GET toutes les 5 s pour refléter la progression, avec un plafond de sécurité.
    maybeTogglePolling() {
        const { prospect } = this.props;
        const running = !!(prospect && prospect.sync && prospect.sync.running);
        if (running && !this.pollTimer) {
            this.pollStartedAt = Date.now();
            this.pollTimer = setInterval(this.pollTick, POLL_INTERVAL_MS);
        } else if (!running && this.pollTimer) {
            this.stopPolling();
            // Fin de synchro : les dates d'envoi par box (synced) ont pu changer.
            this.props.fetchIntegrationBoxes(INTEGRATION_TYPE);
        }
    }

    stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    pollTick = () => {
        if (Date.now() - this.pollStartedAt > MAX_POLL_MS) {
            this.stopPolling();
            return;
        }
        this.props.getIntegration(INTEGRATION_TYPE);
    };

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    handleToggleEnabled = (e) => {
        this.setState({ enabled: e.target.checked });
    };

    handleToggleSimulation = (e) => {
        this.setState({ simulation: e.target.checked });
    };

    // Bascule le marquage "à effacer" d'un secret : vide aussitôt le champ de saisie
    // (on ne veut jamais envoyer une valeur ET l'effacement du même secret).
    handleToggleClear = (clearField, stateField) => () => {
        this.setState((prevState) => ({
            [clearField]: !prevState[clearField],
            [stateField]: '',
        }));
    };

    handleSave = () => {
        const { saveIntegration, prospect } = this.props;
        const {
            enabled, baseUrl, paymentsSince, simulation,
            secretCustomers, secretInstallations, secretPaymentsTs,
            clearCustomers, clearInstallations, clearPaymentsTs,
        } = this.state;

        const secrets = {};
        const clearSecrets = [];
        // Un secret laissé vide n'est pas envoyé : le backend conserve alors la valeur existante.
        // Un secret marqué "à effacer" va dans clearSecrets, jamais dans secrets.
        if (clearCustomers) { clearSecrets.push('customers'); } else if (secretCustomers) { secrets.customers = secretCustomers; }
        if (clearInstallations) { clearSecrets.push('installations'); } else if (secretInstallations) { secrets.installations = secretInstallations; }
        if (clearPaymentsTs) { clearSecrets.push('payments_ts'); } else if (secretPaymentsTs) { secrets.payments_ts = secretPaymentsTs; }

        const settings = { baseUrl, paymentsSince };
        // `simulation` n'est envoyé que si le backend expose outboundEnabled (domainModel récent) :
        // un domainModel antérieur rejette toute clé de réglage inconnue (400 « réglage inconnu
        // "simulation" »), ce qui bloquerait tout enregistrement si appWeb est déployé avant lui.
        if (prospect && prospect.outboundEnabled !== undefined) {
            // Interrupteur forcé coché/désactivé par l'environnement (outboundEnabled === false) :
            // on renvoie la valeur déjà enregistrée côté compte, jamais 'true' forcé, pour ne pas
            // écrire un réglage de compte à cause de l'environnement.
            const simulationToSend = prospect.outboundEnabled === false
                ? !!(prospect.settings && prospect.settings.simulation === 'true')
                : simulation;
            settings.simulation = simulationToSend ? 'true' : 'false';
        }

        const payload = {
            enabled,
            settings,
            secrets,
        };
        if (clearSecrets.length > 0) {
            payload.clearSecrets = clearSecrets;
        }

        saveIntegration(INTEGRATION_TYPE, payload).then(() => {
            // Les secrets saisis ne sont jamais reconservés en state local une fois envoyés,
            // et les marquages "à effacer" sont levés (l'effacement a été appliqué côté serveur).
            this.setState({
                secretCustomers: '', secretInstallations: '', secretPaymentsTs: '',
                clearCustomers: false, clearInstallations: false, clearPaymentsTs: false,
            });
        });
    };

    reloadLogs() {
        this.setState((prevState) => ({ logsReloadKey: prevState.logsReloadKey + 1 }));
    }

    handleTest = () => {
        // Le test peut importer pour de vrai (Source Prospect active) : on relit alors `synced`.
        // Le journal est rechargé dans tous les cas (un test en échec est aussi journalisé).
        this.props.testIntegration(INTEGRATION_TYPE).then(() => {
            this.reloadLogs();
            if (this.props.prospect && !this.props.prospect.testError) {
                this.props.fetchIntegrationBoxes(INTEGRATION_TYPE);
            }
        });
    };

    handleSync = () => {
        // La réponse 202 ne contient que {status:"started"} : on rafraîchit tout de suite le GET
        // pour récupérer sync.running et laisser maybeTogglePolling() prendre le relais.
        this.props.syncIntegration(INTEGRATION_TYPE).then(() => {
            this.props.getIntegration(INTEGRATION_TYPE);
        });
    };

    handleOpenBoxDialog = () => {
        this.setState({ boxDialogOpen: true, cursorsResetNotice: false });
    };

    handleCloseBoxDialog = () => {
        this.setState({ boxDialogOpen: false });
    };

    handleBoxesSaved = (result) => {
        this.setState({ cursorsResetNotice: !!(result && result.cursorsReset) });
    };

    // Liste blanche stricte : seules les box cochées sont envoyées, sélection vide = rien n'est envoyé.
    renderBoxSelection() {
        const { boxes } = this.props;
        const { cursorsResetNotice } = this.state;
        const fetching = !boxes || boxes.fetching;
        const count = (boxes && boxes.selected) ? boxes.selected.length : 0;
        const excluded = (boxes && boxes.excluded) || {};
        const excludedCount = (boxes && boxes.selected) ? boxes.selected.filter((id) => excluded[id]).length : 0;

        return (
            <Box p={1}>
                <Divider style={{ marginBottom: 12 }} />
                <Typography variant="subtitle2" gutterBottom>
                    Box envoyées à Prospect
                </Typography>

                {fetching && !(boxes && boxes.fetched) ? (
                    <Box display="flex" alignItems="center">
                        <CircularProgress size={16} />
                        <Box ml={1}>
                            <Typography variant="body2">Chargement de la sélection...</Typography>
                        </Box>
                    </Box>
                ) : boxes && boxes.error ? (
                    <Typography variant="body2" color="error">{boxes.error}</Typography>
                ) : count > 0 ? (
                    <Typography variant="body2">
                        {count} box sélectionnée{count > 1 ? 's' : ''}
                    </Typography>
                ) : (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                        <WarningRounded style={{ color: '#f57f17', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#f57f17' }}>
                            Aucune box sélectionnée : rien ne sera envoyé.
                        </Typography>
                    </Box>
                )}

                {excludedCount > 0 && (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8, marginTop: 6 }}>
                        <WarningRounded style={{ color: '#e65100', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#e65100' }}>
                            {excludedCount} box sélectionnée{excludedCount > 1 ? 's ont été écartées' : ' a été écartée'} à la
                            dernière synchronisation (détail dans « Sélectionner les box »).
                        </Typography>
                    </Box>
                )}

                {cursorsResetNotice && (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8, marginTop: 6 }}>
                        <WarningRounded style={{ color: '#f57f17', fontSize: 20 }} />
                        <Typography variant="body2" style={{ color: '#f57f17' }}>
                            Des box ont été ajoutées : tout l'historique de leurs paiements depuis la date de
                            départ sera envoyé à la prochaine synchronisation.
                        </Typography>
                    </Box>
                )}

                <Box mt={1}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.handleOpenBoxDialog}
                        disabled={!boxes || !boxes.fetched || !!boxes.error}
                    >
                        Sélectionner les box
                    </Button>
                </Box>

                {this.state.boxDialogOpen && (
                    <ProspectBoxSelectionDialog
                        onClose={this.handleCloseBoxDialog}
                        onSaved={this.handleBoxesSaved}
                    />
                )}
            </Box>
        );
    }

    renderHelp() {
        return (
            <Box mb={1}>
                <Box
                    display="flex"
                    alignItems="flex-start"
                    style={{
                        gap: 8,
                        backgroundColor: '#e3f2fd',
                        borderRadius: 4,
                        padding: 12,
                        marginBottom: 12,
                    }}
                >
                    <InfoOutlinedIcon style={{ color: '#1565c0', fontSize: 20, marginTop: 1 }} />
                    <Typography variant="body2" style={{ color: '#0d47a1' }}>
                        Une fois configurée, cette intégration envoie quotidiennement les clients, les
                        installations et les paiements des box sélectionnées vers Prospect / NEFCO, pour le reporting RBF.
                    </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" component="div">
                    <strong>Que faire :</strong>
                    <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        <li>Dans Prospect, créer une Source « API Push » pour chacune des trois ressources : Clients, Installations, Paiements.</li>
                        <li>Coller ici le secret de chaque Source dans le champ correspondant.</li>
                        <li>Sélectionner les box à envoyer (« Sélectionner les box ») : sans sélection, rien n'est envoyé.</li>
                        <li>Laisser les Sources <strong>inactives</strong> côté Prospect pour tester en mode démo (les données ne sont pas persistées).</li>
                        <li>Cliquer « Tester l'envoi » pour vérifier chaque catégorie, puis « Enregistrer ».</li>
                        <li>Activer l'intégration et « Synchroniser maintenant » une fois prêt.</li>
                    </ol>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Le numéro de téléphone du client est envoyé masqué (les 6 derniers chiffres sont remplacés,
                    ex. +229970xxxxxx). « Tester l'envoi » envoie un échantillon (≤ 20 enregistrements par
                    catégorie) : réellement importé si la Source Prospect correspondante est active, en mode
                    démo sinon.
                </Typography>

                <Divider style={{ margin: '12px 0' }} />
            </Box>
        );
    }

    // Résultat par catégorie (customers/installations/payments_ts), réutilisé pour le test
    // (POST .../test → categories) et pour l'état de la dernière synchro (GET → sync.categories).
    // R = { status: "ok"|"demo"|"simulation"|"error"|"skipped"|"partial", httpCode?, sent, batches, message?,
    //       excluded?: {motif: nombre}, reverted?, recordedFrom?, since?, at }
    // (since absent en mode test ; reverted omis si 0)
    renderCategoryResult(key, result) {
        if (!result) return null;
        const label = CATEGORY_LABELS[key] || key;
        const status = result.status;

        let color = '#616161';
        let Icon = InfoOutlinedIcon;
        let statusLabel = 'Résultat';

        if (status === 'ok') {
            color = '#2e7d32';
            Icon = CheckCircleOutlineRounded;
            statusLabel = 'Importé' + (result.httpCode ? ` (${result.httpCode})` : '');
        } else if (status === 'demo') {
            color = '#1565c0';
            Icon = InfoOutlinedIcon;
            statusLabel = 'Mode démo' + (result.httpCode ? ` (${result.httpCode})` : ' (202)');
        } else if (status === 'simulation') {
            // Couleur distincte du mode démo : aucun appel réseau n'a eu lieu (httpCode 0).
            color = '#455a64';
            Icon = InfoOutlinedIcon;
            statusLabel = 'Simulation';
        } else if (status === 'partial') {
            color = '#f57f17';
            Icon = WarningRounded;
            statusLabel = 'Partiel' + (result.httpCode ? ` (${result.httpCode})` : '') + ' — envoi accepté, enregistrements exclus';
        } else if (status === 'error') {
            color = '#c62828';
            Icon = ErrorOutlineRounded;
            statusLabel = 'Erreur' + (result.httpCode ? ` (${result.httpCode})` : '');
        } else if (status === 'skipped') {
            color = '#757575';
            Icon = WarningRounded;
            statusLabel = 'Ignoré';
        }

        const excludedEntries = result.excluded ? Object.entries(result.excluded) : [];

        return (
            <Box key={key} display="flex" alignItems="flex-start" style={{ gap: 8, marginBottom: 8 }}>
                <Icon style={{ color, fontSize: 20, marginTop: 1 }} />
                <Box>
                    <Typography variant="body2" style={{ fontWeight: 600, color }}>
                        {label} — {statusLabel}
                    </Typography>
                    {result.message && (
                        <Typography variant="body2" color="textSecondary">
                            {result.message}
                        </Typography>
                    )}
                    {typeof result.sent === 'number' && (
                        <Typography variant="body2" color="textSecondary">
                            {result.sent} enregistrement{result.sent > 1 ? 's' : ''} envoyé{result.sent > 1 ? 's' : ''}
                            {typeof result.batches === 'number' && result.batches > 1 ? ` (${result.batches} paquets)` : ''}
                        </Typography>
                    )}
                    {typeof result.reverted === 'number' && result.reverted > 0 && (
                        <Typography variant="body2" color="textSecondary">
                            {result.reverted} paiement{result.reverted > 1 ? 's' : ''} annulé{result.reverted > 1 ? 's' : ''} renvoyé{result.reverted > 1 ? 's' : ''}
                        </Typography>
                    )}
                    {excludedEntries.length > 0 && (
                        <Typography variant="body2" color="textSecondary">
                            Exclus : {excludedEntries.map(([motif, n]) => `${motif} (${n})`).join(', ')}
                        </Typography>
                    )}
                    {result.recordedFrom && (
                        <Typography variant="caption" color="textSecondary" component="div">
                            Fenêtre (date d'enregistrement) depuis {result.recordedFrom}
                        </Typography>
                    )}
                    {result.since && (
                        <Typography variant="caption" color="textSecondary" component="div">
                            Depuis {result.since}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    // Libellé + couleur pour un statut global "success"|"partial"|"error" (test ou synchro).
    statusMeta(status) {
        if (status === 'success') return { color: '#2e7d32', label: 'Réussi' };
        if (status === 'partial') return { color: '#f57f17', label: 'Partiel' };
        if (status === 'error') return { color: '#c62828', label: 'En erreur' };
        return { color: '#616161', label: status || 'Inconnu' };
    }

    renderSyncStatus(sync) {
        if (!sync) return null;
        const categories = sync.categories || {};

        return (
            <Box p={1} style={{ backgroundColor: '#fafafa', borderRadius: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                    État de la dernière synchronisation
                </Typography>

                {sync.running ? (
                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 6 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                            Synchronisation en cours{sync.trigger ? ` (${sync.trigger})` : ''}...
                        </Typography>
                    </Box>
                ) : sync.lastStatus && (
                    <Typography variant="body2" style={{ fontWeight: 600, color: this.statusMeta(sync.lastStatus).color, marginBottom: 4 }}>
                        {this.statusMeta(sync.lastStatus).label}
                    </Typography>
                )}

                {(sync.lastStartedAt || sync.lastFinishedAt) && (
                    <Typography variant="body2" color="textSecondary">
                        {sync.lastStartedAt ? `Démarrée : ${sync.lastStartedAt}` : ''}
                        {sync.lastFinishedAt ? ` — Terminée : ${sync.lastFinishedAt}` : ''}
                    </Typography>
                )}

                {sync.lastError && (
                    <Typography variant="body2" color="error">{sync.lastError}</Typography>
                )}

                {CATEGORY_ORDER.some((key) => categories[key]) && (
                    <Box mt={1}>
                        {CATEGORY_ORDER.map((key) => this.renderCategoryResult(key, categories[key]))}
                    </Box>
                )}

                {sync.cursors && sync.cursors.payments_ts && (
                    <Typography variant="caption" color="textSecondary" component="div">
                        Paiements enregistrés jusqu'au {sync.cursors.payments_ts}
                    </Typography>
                )}
                {sync.cursorsResetAt && (
                    <Typography variant="caption" color="textSecondary" component="div">
                        Curseur remis à zéro le {sync.cursorsResetAt}
                    </Typography>
                )}
            </Box>
        );
    }

    // Message d'erreur : un 503 (envoi désactivé sur cet environnement) est une information
    // attendue, pas une erreur technique — bandeau bleu plutôt que texte rouge.
    renderErrorOrBanner(message, code) {
        if (!message) return null;
        if (code === 503) {
            return (
                <Box p={1} display="flex" alignItems="flex-start" style={{ gap: 8, backgroundColor: '#e3f2fd', borderRadius: 4 }}>
                    <InfoOutlinedIcon style={{ color: '#1565c0', fontSize: 20, marginTop: 1 }} />
                    <Typography variant="body2" style={{ color: '#0d47a1' }}>{message}</Typography>
                </Box>
            );
        }
        return (
            <Box p={1}>
                <Typography variant="body2" color="error">{message}</Typography>
            </Box>
        );
    }

    // Un champ secret + son bouton "Effacer ce secret" (visible seulement si un secret est déjà
    // configuré côté serveur, via secretsMasked). Factorisé pour éviter de répéter le bloc x3.
    renderSecretField(categoryKey, stateField, clearField) {
        const { prospect } = this.props;
        const { loading, saving, secretsMasked } = prospect;
        const masked = secretsMasked || {};
        const value = this.state[stateField];
        const clearing = this.state[clearField];
        const categoryLabel = CATEGORY_LABELS[categoryKey] || categoryKey;

        return (
            <Box>
                <InputText
                    name={stateField}
                    label={`Secret — Source ${categoryLabel} (${categoryKey})`}
                    placeholder={clearing ? 'Ce secret sera effacé à l\'enregistrement' : (masked[categoryKey] || `Saisir le secret de la Source ${categoryLabel}`)}
                    value={value}
                    type="password"
                    onChange={this.handleChange}
                    disabled={loading || saving || clearing}
                    helperText={clearing
                        ? 'Ce secret sera effacé à l\'enregistrement — cliquez « Annuler l\'effacement » pour revenir en arrière.'
                        : 'Le secret n\'est jamais affiché — laisser vide pour ne pas le modifier.'
                            + (categoryKey === 'payments_ts' ? ' Le modifier renvoie tout l\'historique des paiements depuis la date de départ.' : '')}
                />
                {!!masked[categoryKey] && (
                    <Box pl={1} mt={-1} mb={1}>
                        <Button
                            size="small"
                            color={clearing ? 'primary' : 'default'}
                            onClick={this.handleToggleClear(clearField, stateField)}
                            disabled={loading || saving}
                        >
                            {clearing ? "Annuler l'effacement" : 'Effacer ce secret'}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }

    render() {
        const { prospect } = this.props;
        const { enabled, baseUrl, paymentsSince, simulation } = this.state;

        if (!prospect) return null;

        const {
            loading, error,
            saving, saveError,
            testing, testResult, testError, testErrorCode,
            syncing, syncMessage, syncError, syncErrorCode,
            configured, sync,
            outboundEnabled,
        } = prospect;

        // Mode effectif renvoyé par le backend (!outboundEnabled || settings.simulation=="true").
        // Absent sur un ancien domainModel (outboundEnabled non renvoyé) : ne jamais supposer la
        // simulation dans ce cas, `prospect.simulation` vaut alors undefined → false.
        const simulationEffective = !!prospect.simulation;
        // Interrupteur affiché coché et désactivé quand l'environnement interdit l'envoi, quelle
        // que soit la valeur saisie de settings.simulation.
        const simulationSwitchChecked = outboundEnabled === false ? true : simulation;
        // Valeur enregistrée côté compte, pour détecter un interrupteur modifié mais pas encore
        // enregistré. Non pertinent quand l'environnement force l'interrupteur (outboundEnabled
        // === false) : dans ce cas, enregistrer ne change rien à la valeur du compte (voir handleSave).
        const savedSimulation = !!(prospect.settings && prospect.settings.simulation === 'true');
        const simulationDirty = outboundEnabled !== false && simulation !== savedSimulation;

        return (
            <Box>
                {this.renderHelp()}

                {loading && (
                    <Box display="flex" alignItems="center" p={1}>
                        <CircularProgress size={20} />
                        <Box ml={1}>
                            <Typography variant="body2">Chargement...</Typography>
                        </Box>
                    </Box>
                )}

                {error && (
                    <Box p={1}>
                        <Typography variant="body2" color="error">{error}</Typography>
                    </Box>
                )}

                <Box p={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={this.handleToggleEnabled}
                                disabled={loading || saving}
                                color="primary"
                            />
                        }
                        label={enabled ? 'Intégration active' : 'Intégration inactive'}
                    />
                </Box>

                {outboundEnabled === false && (
                    <Box p={1}>
                        <Box
                            display="flex"
                            alignItems="flex-start"
                            style={{
                                gap: 8,
                                backgroundColor: '#e3f2fd',
                                borderRadius: 4,
                                padding: 12,
                            }}
                        >
                            <InfoOutlinedIcon style={{ color: '#1565c0', fontSize: 20, marginTop: 1 }} />
                            <Typography variant="body2" style={{ color: '#0d47a1' }}>
                                Envois désactivés sur cet environnement : le test et la synchronisation
                                tournent en simulation (journal et CSV, rien n'est envoyé à Prospect).
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Box p={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={simulationSwitchChecked}
                                onChange={this.handleToggleSimulation}
                                disabled={loading || saving || outboundEnabled === false}
                                color="primary"
                            />
                        }
                        label="Mode simulation (aucun envoi à Prospect — journal et CSV seulement)"
                    />
                    {simulationDirty && (
                        <Typography variant="caption" color="textSecondary" component="div" style={{ marginLeft: 46 }}>
                            Enregistrer pour appliquer
                        </Typography>
                    )}
                </Box>

                <InputText
                    name="baseUrl"
                    label="URL de base"
                    placeholder={DEFAULT_BASE_URL}
                    value={baseUrl}
                    onChange={this.handleChange}
                    disabled={loading || saving}
                    helperText="Doit être un domaine https://*.prospect.energy — imposé côté serveur. Modifier ce champ renvoie tout l'historique des paiements depuis la date de départ."
                />

                <InputText
                    name="paymentsSince"
                    label="Date de départ des paiements"
                    value={paymentsSince}
                    type="date"
                    // Le navigateur affiche toujours « jj/mm/aaaa » : sans shrink, le libellé
                    // se superpose au gabarit tant que la valeur est vide.
                    InputLabelProps={{ shrink: true }}
                    onChange={this.handleChange}
                    disabled={loading || saving}
                    helperText="Obligatoire avant toute synchronisation réelle : sans date de départ, aucun historique de paiements n'est envoyé par défaut. Modifier ce champ renvoie tout l'historique des paiements depuis la nouvelle date."
                />

                {this.renderSecretField('customers', 'secretCustomers', 'clearCustomers')}
                {this.renderSecretField('installations', 'secretInstallations', 'clearInstallations')}
                {this.renderSecretField('payments_ts', 'secretPaymentsTs', 'clearPaymentsTs')}

                {this.renderBoxSelection()}

                {/* Résultat du test, par catégorie */}
                {testResult && (
                    <Box p={1}>
                        {testResult.status && (
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600, color: this.statusMeta(testResult.status).color, marginBottom: 4 }}
                            >
                                Test — {this.statusMeta(testResult.status).label}
                            </Typography>
                        )}
                        {CATEGORY_ORDER.map((key) => this.renderCategoryResult(key, testResult.categories[key]))}
                    </Box>
                )}
                {this.renderErrorOrBanner(testError, testErrorCode)}

                {saveError && (
                    <Box p={1}>
                        <Typography variant="body2" color="error">{saveError}</Typography>
                    </Box>
                )}

                {syncMessage && (
                    <Box p={1}>
                        <Typography variant="body2" style={{ color: '#2e7d32' }}>{syncMessage}</Typography>
                    </Box>
                )}
                {this.renderErrorOrBanner(syncError, syncErrorCode)}

                {sync && (
                    <Box p={1}>
                        {this.renderSyncStatus(sync)}
                    </Box>
                )}

                {enabled && !paymentsSince && (
                    <Box p={1}>
                        <Typography variant="body2" style={{ color: '#f57f17' }}>
                            Renseignez la date de départ des paiements avant de synchroniser (obligatoire pour
                            une synchronisation réelle — voir l'aide du champ ci-dessus).
                        </Typography>
                    </Box>
                )}

                <Box display="flex" alignItems="center" p={1} style={{ gap: 8 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.handleTest}
                        disabled={loading || saving || testing || (!configured && !simulationEffective)}
                        startIcon={testing ? <CircularProgress size={16} /> : null}
                    >
                        {testing ? 'Test en cours...' : "Tester l'envoi"}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={this.handleSave}
                        disabled={loading || saving}
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                    >
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={this.handleSync}
                        disabled={loading || saving || syncing || !enabled || !paymentsSince || (sync && sync.running) || (!configured && !simulationEffective)}
                        startIcon={(syncing || (sync && sync.running)) ? <CircularProgress size={16} /> : null}
                    >
                        {syncing || (sync && sync.running) ? 'Synchronisation...' : 'Synchroniser maintenant'}
                    </Button>
                    {simulationEffective && (
                        <Chip
                            size="small"
                            label="Simulation"
                            style={{ backgroundColor: '#455a64', color: '#fff' }}
                        />
                    )}
                </Box>

                <ProspectPushLogTable reloadKey={this.state.logsReloadKey} categoryLabels={CATEGORY_LABELS} />
            </Box>
        );
    }
}

function mapStateToProps(state) {
    return {
        prospect: state.settings && state.settings.integrations && state.settings.integrations[INTEGRATION_TYPE],
        boxes: state.settings && state.settings.boxes && state.settings.boxes[INTEGRATION_TYPE],
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getIntegration,
        saveIntegration,
        testIntegration,
        syncIntegration,
        fetchIntegrationBoxes,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ProspectIntegrationForm);
